import React from 'react';
import { X, Store, Truck, Bell, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePushNotifications } from '@/context/PushNotificationContext';
import { formatPrice } from '@/lib/formatPrice';
import { isTodayRome } from '@/lib/utils';

// Format time in Italian (Rome timezone)
const formatTime = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('it-IT', {
    timeZone: 'Europe/Rome',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

// Normalize phone for WhatsApp
const normalizePhone = (phone) => {
  if (!phone) return null;
  let cleaned = phone.replace(/[\s+\-.()\[\]]/g, '');
  if (cleaned.startsWith('00')) cleaned = cleaned.substring(2);
  if (cleaned.startsWith('0') && cleaned.length <= 11) cleaned = '39' + cleaned.substring(1);
  if (!cleaned.startsWith('39') && cleaned.length === 10) cleaned = '39' + cleaned;
  if (cleaned.startsWith('3') && cleaned.length === 10) cleaned = '39' + cleaned;
  return cleaned;
};

export function PushNotificationStack() {
  const { pushNotifications, removePushNotification, MAX_PUSH_NOTIFICATIONS } = usePushNotifications();

  // Safety filter: only today's notifications, sorted, limited
  const displayNotifications = pushNotifications
    .filter(n => isTodayRome(n.created_at))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, MAX_PUSH_NOTIFICATIONS);

  if (displayNotifications.length === 0) {
    return null;
  }

  const openWhatsApp = (phone, customerName, total) => {
    const normalizedPhone = normalizePhone(phone);
    if (!normalizedPhone) return;
    
    const message = encodeURIComponent(
      `Ciao ${customerName}! Grazie per il tuo ordine da Sapori 1989. Il totale è ${formatPrice(total)}. Ti confermiamo la ricezione!`
    );
    window.open(`https://wa.me/${normalizedPhone}?text=${message}`, '_blank');
  };

  return (
    <div 
      className="fixed top-4 right-4 z-50 flex flex-col gap-3 max-w-sm w-full pointer-events-none"
      data-testid="push-notification-stack"
    >
      {displayNotifications.map((notification, index) => (
        <div
          key={notification.id}
          data-testid={`push-notification-${notification.id}`}
          className="pointer-events-auto animate-in slide-in-from-right-5 fade-in-0 duration-300 bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-orange-200 dark:border-orange-800 overflow-hidden"
          style={{ 
            animationDelay: `${index * 100}ms`,
            opacity: 1 - (index * 0.15) // Slightly fade older notifications
          }}
        >
          {/* Orange accent bar */}
          <div className="h-1 w-full gradient-instagram" />
          
          <div className="p-4">
            {/* Header */}
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full gradient-instagram flex items-center justify-center flex-shrink-0">
                  <Bell className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-foreground">
                    {notification.title || 'Nuovo ordine'}
                  </h4>
                  <span className="text-xs text-muted-foreground">
                    {formatTime(notification.created_at)}
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 flex-shrink-0 hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={() => removePushNotification(notification.id)}
                data-testid={`push-notification-dismiss-${notification.id}`}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Content */}
            <div className="flex items-center gap-2 mb-3">
              {/* Delivery type badge */}
              {notification.delivery_type === 'PICKUP' ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-emerald-500 text-white">
                  <Store className="w-3 h-3" />
                  Ritiro
                </span>
              ) : notification.delivery_type === 'DELIVERY' ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-500 text-white">
                  <Truck className="w-3 h-3" />
                  Consegna
                </span>
              ) : null}
              
              {/* Customer name */}
              {notification.customer_name && (
                <span className="text-sm font-medium text-foreground truncate">
                  {notification.customer_name}
                </span>
              )}
            </div>

            {/* Total and action */}
            <div className="flex items-center justify-between">
              {notification.total > 0 && (
                <span className="text-lg font-bold gradient-instagram-text">
                  {formatPrice(notification.total)}
                </span>
              )}
              
              {notification.customer_phone && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-green-600 border-green-600 hover:bg-green-50 dark:hover:bg-green-950"
                  onClick={() => openWhatsApp(
                    notification.customer_phone,
                    notification.customer_name,
                    notification.total
                  )}
                  data-testid={`push-notification-whatsapp-${notification.id}`}
                >
                  <MessageCircle className="w-4 h-4 mr-1" />
                  WhatsApp
                </Button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
